import { useCallback, useMemo, useState } from 'react';
import type {
  DirectedCastDescription,
  DirectedCastEntry,
  DirectedCastRole,
} from 'fym-shared/src/music24/directedVideoApi';
import { directedActionError, type DirectedActionError } from '../directedErrors';
import {
  useRemoveDirectedCastMutation,
  useUpdateDirectedCastMutation,
  useWriteDirectedStoriesMutation,
  type DirectedVideoArgs,
} from '../directedVideoApi';
import { castNeedsConsent, initialConsent, isUploadedPhoto } from './castRules';

/**
 * The edits of the cast step (design b1, b4): the label and the role of an entry, its removal, the
 * consent tick and "Write 3 stories".
 *
 * Consent (design b4, review I3): every uploaded photo needs the tick, whatever its role, so an
 * upload waits for it (`useCastUpload`) and a cast with an upload cannot go on without it. A change to
 * Person while the box is not ticked sends nothing: the card keeps Person as a wish and the box shows
 * its error. The tick then sends each wish with `consent: true`.
 */
export function useCastActions(args: DirectedVideoArgs, cast: readonly DirectedCastEntry[]) {
  const [updateCast] = useUpdateDirectedCastMutation();
  const [removeCast] = useRemoveDirectedCastMutation();
  const [writeStories, writing] = useWriteDirectedStoriesMutation();
  const [consent, setConsentState] = useState(() => initialConsent(cast));
  const [wishes, setWishes] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<DirectedActionError | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    try {
      await action();
      return true;
    } catch (caught) {
      console.error('Directed video: a cast edit failed', caught);
      setError(directedActionError(caught));
      return false;
    }
  }, []);

  const setLabel = useCallback(
    (castId: string, label: string) => run(() => updateCast({ ...args, castId, label }).unwrap()),
    [args, run, updateCast]
  );

  const setDescription = useCallback(
    (castId: string, description: DirectedCastDescription | null) =>
      run(() => updateCast({ ...args, castId, description }).unwrap()),
    [args, run, updateCast]
  );

  const setRole = useCallback(
    (entry: DirectedCastEntry, role: DirectedCastRole) => {
      if (role === 'person' && isUploadedPhoto(entry) && !consent) {
        setWishes((current) => new Set(current).add(entry.id));
        return Promise.resolve(false);
      }
      setWishes((current) => {
        const next = new Set(current);
        next.delete(entry.id);
        return next;
      });
      return run(() =>
        updateCast({ ...args, castId: entry.id, role, ...(role === 'person' ? { consent: true } : {}) }).unwrap()
      );
    },
    [args, consent, run, updateCast]
  );

  const remove = useCallback(
    (castId: string) => run(() => removeCast({ ...args, castId }).unwrap()),
    [args, removeCast, run]
  );

  const setConsent = useCallback(
    (next: boolean) => {
      setConsentState(next);
      if (!next || wishes.size === 0) {
        return;
      }
      const ids = [...wishes];
      setWishes(new Set());
      ids.forEach((castId) => {
        run(() => updateCast({ ...args, castId, role: 'person', consent: true }).unwrap()).catch(console.error);
      });
    },
    [args, run, updateCast, wishes]
  );

  const needsConsent = castNeedsConsent(cast, wishes.size);
  const consentMissing = needsConsent && !consent;

  const startStories = useCallback(() => run(() => writeStories(args).unwrap()), [args, run, writeStories]);

  // "Make it with no cast": the cast goes, then the director writes the stories with no cast.
  const startWithNoCast = useCallback(async () => {
    setIsClearing(true);
    try {
      for (const entry of cast) {
        // One after the other: each write holds the lock of the video.
        // eslint-disable-next-line no-await-in-loop
        const ok = await run(() => removeCast({ ...args, castId: entry.id }).unwrap());
        if (!ok) {
          return false;
        }
      }
      return await run(() => writeStories(args).unwrap());
    } finally {
      setIsClearing(false);
    }
  }, [args, cast, removeCast, run, writeStories]);

  const clearError = useCallback(() => setError(null), []);

  return useMemo(
    () => ({
      consent,
      setConsent,
      wishes,
      needsConsent,
      consentMissing,
      setLabel,
      setDescription,
      setRole,
      remove,
      startStories,
      startWithNoCast,
      isStarting: writing.isLoading || isClearing,
      error,
      clearError,
    }),
    [
      clearError,
      consent,
      consentMissing,
      error,
      isClearing,
      needsConsent,
      remove,
      setConsent,
      setDescription,
      setLabel,
      setRole,
      startStories,
      startWithNoCast,
      wishes,
      writing.isLoading,
    ]
  );
}
